import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SSOCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin } = useAuth();
  const [status, setStatus] = useState("validating");
  const [error, setError] = useState(null);
  
  // Ref to prevent multiple API calls
  const hasValidated = useRef(false);

  useEffect(() => {
    const validateSSO = async () => {
      // Prevent multiple validation attempts
      if (hasValidated.current) {
        return;
      }
      
      const token = searchParams.get("token");
      
      if (!token) {
        setStatus("error");
        setError("No SSO token provided");
        return;
      }

      // Mark as validated to prevent duplicate calls
      hasValidated.current = true;

      try {
        setStatus("validating");
        
        const response = await fetch(`${API_URL}/api/auth/sso/callback?token=${token}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || "SSO validation failed");
        }

        // Login successful - store the token and user data
        ssoLogin(data.access_token, data.user);
        
        setStatus("success");
        toast.success("Login successful!");
        
        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1000);

      } catch (err) {
        console.error("SSO Error:", err);
        setStatus("error");
        setError(err.message || "SSO login failed");
        toast.error(err.message || "SSO login failed");
      }
    };

    validateSSO();
  }, [searchParams, ssoLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full mx-4 text-center">
        {status === "validating" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Validating SSO Token</h2>
            <p className="text-slate-400">Please wait while we log you in...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Login Successful!</h2>
            <p className="text-slate-400">Redirecting to dashboard...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">SSO Login Failed</h2>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
              data-testid="sso-go-to-login-btn"
            >
              Go to Login Page
            </button>
          </>
        )}
      </div>
    </div>
  );
};
